import React from 'react';
import { motion } from 'framer-motion';
import { IconType } from 'react-icons';
import { FaGlobe, FaLaptopCode, FaMobile, FaCogs, FaServer, FaShieldAlt, FaDesktop } from 'react-icons/fa';
import { GiCctvCamera } from "react-icons/gi";
import AnimatedButton from '../shared/AnimatedButton';
import AnimatedHeading from '../shared/AnimatedHeading';
import { Link } from 'react-router-dom';

// Updated interface with proper typing
interface Service {
  title: string;
  description: string;
  icon: IconType;
}

const services: Service[] = [
  {
    title: "Internet Services",
    description: "Secure domain registration, web hosting, and business email solutions.",
    icon: FaGlobe
  },
  {
    title: "Software Solutions",
    description: "Custom web and desktop applications, API development, and seamless integrations.",
    icon: FaLaptopCode
  },
  {
    title: "Mobile App Development",
    description: "Native and cross-platform mobile applications tailored to your business needs.",
    icon: FaMobile
  },
  {
    title: "Web App Development",
    description: "Build secure, scalable, and high-performing web applications.",
    icon: FaDesktop
  },
  {
    title: "ERP Consultation",
    description: "Expert guidance on ERP implementation, customization, and ongoing support.",
    icon: FaCogs
  },
  {
    title: "CCTV & Surveillance Solutions",
    description: "Installation, maintenance, and monitoring systems for your security.",
    icon: GiCctvCamera
  },
  {
    title: "Hardware Solutions",
    description: "Installation, maintenance, upgrades, and secure hardware recycling.",
    icon: FaServer
  },
  {
    title: "Security Solutions",
    description: "Advanced cybersecurity services, including data protection and secure erasure.",
    icon: FaShieldAlt
  }
];

const ServicesSection = () => {
  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5
      }
    }
  };

  return (
    <section className="relative py-24 bg-gradient-to-br from-gray-50 to-gray-100 overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 2px 2px, blue 1px, transparent 0)`,
          backgroundSize: '32px 32px',
        }}></div>
      </div>

      <div className="container mx-auto px-6 relative z-10">
        <h5 className="text-md font-bold text-center mb-2 text-transparent bg-clip-text bg-gradient-to-r from-blue-500 to-purple-500">
          Our Services
        </h5>
        <AnimatedHeading text="What We Offer" />
        
        <div className="grid md:grid-cols-3 gap-8 mt-16 justify-items-center">
          {services.map((service, index) => (
            <motion.div
              key={index}
              variants={cardVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className={`group bg-white p-8 rounded-xl shadow-lg hover:shadow-xl 
                transition duration-300 transform hover:-translate-y-1 ${
                  index === services.length - 1 && services.length % 3 !== 0
                    ? ''
                    : ''
                }`}
            >
              <div className="text-center">
                <div className="w-12 h-12 mx-auto mb-4">
                  {React.createElement(service.icon, {
                    className: "w-full h-full text-blue-600 group-hover:scale-110 transition-transform"
                  })}
                </div>
                <h3 className="text-xl font-semibold mb-3 text-gray-800">{service.title}</h3>
                <p className="text-gray-600 leading-relaxed">{service.description}</p>
              </div>
              
              <div className="mt-6 text-center">
                <motion.div whileHover={{ x: 5 }}>
                  <Link
                    to={`/services/${service.title.toLowerCase().replace(/\s+/g, '-')}`}
                    className="inline-flex items-center text-blue-600 hover:text-blue-700 
                      font-medium group"
                  >
                    Learn More
                    <svg
                      className="w-4 h-4 ml-2 transform transition-transform group-hover:translate-x-1"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </Link>
                </motion.div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* See More Button */}
        <motion.div 
          className="text-center mt-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <AnimatedButton 
            to="/services" 
            text="Explore All Services" 
          />
        </motion.div>
      </div>
    </section>
  );
};

export default ServicesSection;